/**
 * PaisSelect - Componente para seleccionar Paises/provincias
 *
 * Responsabilidades:
 * - Cargar Paises según el país seleccionado
 * - Gestionar Pais de carga y errores
 */

import { useEffect, useState, useCallback,useRef } from 'react';
import { Controller } from 'react-hook-form';
import { getPaises } from '@/core/api/sigma';
import { FilterableSelectProps, SelectOption } from './types';
import { getErrorMessage } from './utils';
import { Typeahead } from 'react-bootstrap-typeahead';
import { createRenderMenuWithSearch, createMenuToggleHandler } from './typeaheadUtils';

interface PaisSelectProps extends FilterableSelectProps {
  // Puede agregar props específicas si es necesario
}

export function PaisSelect({
  control,
  errors,
  name,
  label = 'País',
  placeholder = 'Seleccione un Pais',
  disabled = false,
  required = false,
  setData,
  hideArrowWhenDisabled=true,
  filters = {},
  setValue
  
}: PaisSelectProps) {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { cpais  } = filters;


  /**
   * Carga Paises desde API
   */
  const fetchPais = useCallback(async () => {

    setLoading(true);
    setError(null);

    try {
      const Paises = await getPaises();
      const newOptions = Paises.map(Pais => ({
        value: Pais.cpais,
        label: Pais.xpais
      })).sort((a, b) => a.label.localeCompare(b.label));

      setOptions(newOptions);

      if (setData) {
        setData((prevData: any) => ({
          ...prevData,
          [name]: newOptions
        }));
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Error al cargar Paises');
      setError(errorMsg);
      console.error(errorMsg, err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [name, setData]);

  useEffect(() => {
    if (cpais && setValue && options.length > 0) {
      const currentExists = options.some(option =>
        option.value === String(cpais)
      );
      
      if (!currentExists) {
        setValue(name, '');
        }
        else {
          setValue(name, String(cpais));
          
        }
      
    }
  }, [cpais, setValue, name, options]);

  /**
   * Efecto: cargar Paises cuando cambia el país
   */
  useEffect(() => {
    fetchPais();
  }, [fetchPais]);

 // Crear función renderMenu reutilizable (3 líneas!)
   const [searchQuery, setSearchQuery] = useState('');
   const searchInputRef = useRef<HTMLInputElement>(null);
   const renderMenu = createRenderMenuWithSearch({
     searchQuery,
     setSearchQuery,
     searchInputRef,
     options,
     placeholder: 'Buscar '+label
   });
 
   // Crear manejador de menú reutilizable (1 línea!)
   const handleMenuToggle = createMenuToggleHandler(searchInputRef, setSearchQuery);
 
 
   return (
     <div className="form-group">
       {label && (
         <label htmlFor={name}>
           {label} {required && <span className="text-danger">*</span>}
         </label>
       )}
       <Controller
         name={name}
         control={control}
         rules={{ required: required ? `${label} es requerido` : false }}
         render={({ field: rhfField }) => (
           <div className="typeahead-wrapper-sd position-relative">
             <Typeahead
               id={name}
               options={options}
               labelKey="label"
               placeholder={loading ? 'Cargando...' : placeholder}
               clearButton
               disabled={disabled || loading}
               isInvalid={!!errors?.[name]}
               
               onChange={(selected: any[]) => {
                 const value = selected[0]?.value || '';
                 rhfField.onChange(value);
                 setSearchQuery('');
               }}
               
               selected={options.filter(opt => opt.value === rhfField.value)}
               onBlur={rhfField.onBlur}
               ref={rhfField.ref}
               
               inputProps={{
                 name: rhfField.name,
                 className: 'form-control',
                 readOnly: true
               }}
               
               className={errors?.[name] ? 'is-invalid' : ''}
               
               // 🎯 Usar funciones reutilizables
               renderMenu={renderMenu}
               onMenuToggle={handleMenuToggle}
             />
             
           {(!disabled && hideArrowWhenDisabled) && (
               <i className="typeahead-arrow fas fa-chevron-down"></i>
             )}
            </div>
         )}
       />
       {errors?.[name] && (
         <div className="invalid-feedback d-block">
           {errors[name]?.message as string}
         </div>
       )}
       {error && (
         <div className="text-danger small mt-1">
           {error}
         </div>
       )}
     </div>
   );
 }
 